/**
 * partners-affiliate.js
 * =====================
 * Plarium Affiliate Page — Minimal Vanilla JavaScript
 *
 * Scope: UI enhancements only.
 *   - FAQ accordion (open/close with smooth animation)
 *   - Smooth scroll for in-page anchor CTAs
 *   - Optional: animate metric counters on viewport entry (progressive enhancement)
 *
 * No framework dependencies. No external libraries.
 * All code is scoped to elements inside .plarium-affiliate-page.
 * Safe to load deferred. Will not throw if DOM elements are absent.
 *
 * Integration note:
 *   Load this file with <script src="partners-affiliate.js" defer>
 *   or include it in your existing JS bundle — all functions are IIFE-wrapped.
 */

(function () {
    'use strict';

    // ============================================================
    // CONSTANTS
    // ============================================================

    /** Root namespace selector — matches the CSS wrapper */
    var ROOT_SELECTOR = '.plarium-affiliate-page';

    /** Data attributes used to select FAQ elements */
    var FAQ_ITEM_ATTR    = 'data-paf-faq-item';
    var FAQ_TRIGGER_ATTR = 'data-paf-faq-trigger';
    var FAQ_BODY_ATTR    = 'data-paf-faq-body';

    /** Class toggled on open FAQ items */
    var FAQ_OPEN_CLASS = 'paf-faq-item--open';


    // ============================================================
    // UTILITY: Safe querySelectorAll scoped to root
    // ============================================================

    /**
     * Returns all elements matching `selector` inside the page root.
     * Returns an empty array if root is not found.
     *
     * @param {string} selector
     * @returns {Element[]}
     */
    function $$(selector) {
        var root = document.querySelector(ROOT_SELECTOR);
        if (!root) return [];
        return Array.prototype.slice.call(root.querySelectorAll(selector));
    }


    // ============================================================
    // MODULE: FAQ ACCORDION
    // ============================================================

    /**
     * Initialises the FAQ accordion.
     *
     * Behaviour:
     *  - Clicking a trigger toggles the corresponding body open/closed.
     *  - Closing one item does NOT force-close others (multi-open allowed).
     *    If single-open is needed, uncomment the `closeAllExcept` call below.
     *  - Uses `scrollHeight` for smooth CSS max-height animation without
     *    JavaScript animation frames.
     *  - Updates aria-expanded for screen readers.
     */
    function initFaqAccordion() {

        /** @type {NodeList} */
        var items = $$('[' + FAQ_ITEM_ATTR + ']');

        if (!items.length) return;

        items.forEach(function (item) {

            var trigger = item.querySelector('[' + FAQ_TRIGGER_ATTR + ']');
            var body    = item.querySelector('[' + FAQ_BODY_ATTR + ']');

            if (!trigger || !body) return;

            // Ensure collapsed state on init (CSS sets max-height:0, but explicit is safer)
            body.style.maxHeight = '0';

            trigger.addEventListener('click', function () {

                var isOpen = item.classList.contains(FAQ_OPEN_CLASS);

                // OPTIONAL: Uncomment to enforce single-open accordion behaviour.
                // closeAllExcept(item, items);

                if (isOpen) {
                    closeItem(item, trigger, body);
                } else {
                    openItem(item, trigger, body);
                }

            });

        });

    }

    /**
     * Opens a single FAQ item.
     *
     * @param {Element} item
     * @param {Element} trigger
     * @param {Element} body
     */
    function openItem(item, trigger, body) {
        item.classList.add(FAQ_OPEN_CLASS);
        trigger.setAttribute('aria-expanded', 'true');

        // Set max-height to scrollHeight to allow CSS transition
        body.style.maxHeight = body.scrollHeight + 'px';
    }

    /**
     * Closes a single FAQ item.
     *
     * @param {Element} item
     * @param {Element} trigger
     * @param {Element} body
     */
    function closeItem(item, trigger, body) {
        item.classList.remove(FAQ_OPEN_CLASS);
        trigger.setAttribute('aria-expanded', 'false');
        body.style.maxHeight = '0';
    }

    /**
     * Closes all FAQ items except one.
     * Uncomment the call inside the click handler to enable single-open mode.
     *
     * @param {Element}   exceptItem - The item to leave open.
     * @param {Element[]} allItems   - All FAQ item elements.
     */
    function closeAllExcept(exceptItem, allItems) { // eslint-disable-line no-unused-vars
        allItems.forEach(function (item) {
            if (item === exceptItem) return;
            var trigger = item.querySelector('[' + FAQ_TRIGGER_ATTR + ']');
            var body    = item.querySelector('[' + FAQ_BODY_ATTR + ']');
            if (trigger && body) {
                closeItem(item, trigger, body);
            }
        });
    }


    // ============================================================
    // MODULE: SMOOTH SCROLL FOR IN-PAGE ANCHORS
    // ============================================================

    /**
     * Intercepts clicks on anchor links whose href starts with "#paf-"
     * and applies smooth scrolling with an offset to account for
     * potential fixed site headers on the host page.
     *
     * INTEGRATION NOTE: Adjust HEADER_OFFSET if the Plarium site header
     * height differs from the default below.
     */
    function initSmoothScroll() {

        /** Pixels to offset from top — adjust to match host site header height */
        var HEADER_OFFSET = 80;

        var root = document.querySelector(ROOT_SELECTOR);
        if (!root) return;

        // Listen on the root element to catch all anchor clicks within the block
        root.addEventListener('click', function (event) {

            var target = event.target.closest('a[href^="#"]');
            if (!target) return;

            var href = target.getAttribute('href');
            if (!href || href === '#') return;

            var destinationEl = document.querySelector(href);
            if (!destinationEl) return;

            event.preventDefault();

            var top = destinationEl.getBoundingClientRect().top
                + window.pageYOffset
                - HEADER_OFFSET;

            window.scrollTo({
                top: top,
                behavior: 'smooth'
            });

        });

    }


    // ============================================================
    // MODULE: METRIC COUNTER ANIMATION (Progressive Enhancement)
    // ============================================================

    /**
     * Animates numeric metric values when they scroll into the viewport.
     * Uses IntersectionObserver — degrades gracefully on older browsers
     * (values will simply be shown statically if the API is unavailable).
     *
     * Targets elements with [data-paf-count-to] attribute.
     * The attribute value should be the target number (integer or float).
     *
     * Usage in HTML:
     *   <div class="paf-metric-item__value" data-paf-count-to="600">600M+</div>
     *
     * NOTE: Currently metrics use text like "600M+" which contains a suffix.
     * This module reads the data attribute value and animates from 0 to that
     * number, then restores the original text. Opt-in per element via
     * data-paf-count-to.
     */
    function initMetricCounters() {

        if (!window.IntersectionObserver) return;

        var counterEls = $$('[data-paf-count-to]');
        if (!counterEls.length) return;

        var observer = new IntersectionObserver(function (entries) {
            entries.forEach(function (entry) {
                if (!entry.isIntersecting) return;

                var el        = entry.target;
                var targetVal = parseFloat(el.getAttribute('data-paf-count-to'));
                var suffix    = el.getAttribute('data-paf-count-suffix') || '';
                var duration  = 1400; // ms
                var startTime = null;

                observer.unobserve(el); // Animate once only

                function step(timestamp) {
                    if (!startTime) startTime = timestamp;
                    var progress = Math.min((timestamp - startTime) / duration, 1);
                    // Ease-out curve
                    var eased    = 1 - Math.pow(1 - progress, 3);
                    var current  = Math.floor(eased * targetVal);

                    el.textContent = current + suffix;

                    if (progress < 1) {
                        requestAnimationFrame(step);
                    } else {
                        el.textContent = targetVal + suffix;
                    }
                }

                requestAnimationFrame(step);
            });
        }, {
            threshold: 0.4
        });

        counterEls.forEach(function (el) {
            observer.observe(el);
        });

    }


    // ============================================================
    // INIT: Run all modules on DOM ready
    // ============================================================

    /**
     * Bootstraps all page modules.
     * Runs after DOM is ready. Safe to call multiple times
     * (each module checks for element presence before acting).
     */
    function init() {
        initFaqAccordion();
        initSmoothScroll();
        initMetricCounters(); // Optional progressive enhancement
    }

    // Respect defer attribute — DOM will be ready when this runs.
    // Fallback for edge cases where script is not deferred.
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

}());